import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Sparkles, UserCheck, Clock3, Pencil, KeyRound, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getApiUrl } from '../config/api';

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const toTitleCase = (value) =>
  value
    ? value
        .toLowerCase()
        .replace(/[_-]/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : '';

const getPlanName = (subscription) => {
  if (!subscription) return 'Free Plan';
  if (subscription.plan_name) return subscription.plan_name;
  if (subscription.plan_type) return `${toTitleCase(subscription.plan_type)} Plan`;
  return 'Free Plan';
};

const getBillingCycle = (subscription) => {
  if (!subscription) return 'N/A';
  return subscription.billing_cycle ? toTitleCase(subscription.billing_cycle) : 'N/A';
};

const getRenewalDate = (subscription) => {
  if (!subscription) return 'N/A';
  return (
    subscription.end_date ||
    subscription.renewal_date ||
    subscription.expiry_date ||
    subscription.current_period_end ||
    subscription.next_billing_date ||
    'N/A'
  );
};

const getSubscriptionFeatures = (subscription) => {
  const defaults = {
    free: [
      'Access to limited research summaries',
      'Weekly market newsletter',
      'Basic portfolio alerts',
    ],
    basic: [
      'Basic research reports',
      'Segment financials',
      'Weekly market summary',
    ],
    premium: [
      'Everything in Basic',
      'Premium reports',
      'DCF and scenario analysis',
      'Priority research alerts',
    ],
  };

  if (subscription?.features && subscription.features.length > 0) {
    return subscription.features;
  }

  if (!subscription?.plan_type) {
    return defaults.free;
  }

  const type = subscription.plan_type.toLowerCase();
  if (type.includes('premium')) return defaults.premium;
  if (type.includes('basic')) return defaults.basic;
  return defaults.free;
};

const getDisplayName = (user) => {
  return user?.name || user?.full_name || user?.email || 'User';
};

const getAvatarText = (user) => {
  const name = user?.name || user?.full_name || user?.email || 'U';
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
  return initials || 'U';
};

const isAdminUser = (user) =>
  String(user?.role || '').trim().toUpperCase().replace(/^ROLE_/, '') === 'ADMIN_USER';

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [autoRenewalLoading, setAutoRenewalLoading] = useState(false);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const [profileEdit, setProfileEdit] = useState({ name: '', picture: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });

  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState({ type: '', text: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const refreshData = useCallback(async () => {
    const redirectTarget = `${window.location.pathname}${window.location.search}`;
    try {
      const API = getApiUrl();
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const userResp = await fetch(`${API}/auth/me`, {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      if (!userResp.ok) {
        navigate(`/signin?redirect=${encodeURIComponent(redirectTarget)}`, { replace: true });
        return;
      }

      const userData = await userResp.json();
      setUser(userData);

      try {
        const subscriptionResp = await fetch(`${API}/payments/subscription/current`, {
          method: 'GET',
          headers,
          credentials: 'include',
        });
        if (subscriptionResp.ok && subscriptionResp.status !== 204) {
          const text = await subscriptionResp.text();
          const subscriptionData = text ? JSON.parse(text) : null;
          setSubscription(subscriptionData);
        } else {
          setSubscription(null);
        }
      } catch (_subErr) {
        setSubscription(null);
      }

      setProfileEdit({ name: userData.name || '', picture: userData.picture || '' });
      setStatus('ready');
    } catch (fetchError) {
      console.error('Profile fetch failed:', fetchError);
      setError('Unable to load profile information. Please sign in again.');
      setStatus('error');
    }
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setPaymentSuccess(true);
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setPaymentSuccess(false), 5000);
    }
    refreshData();
  }, [refreshData]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshData();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [refreshData]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#F7F5F2] flex items-center justify-center px-6 py-12">
        <div className="text-center px-6 py-10 rounded-3xl border border-slate-200 bg-white shadow-lg">
          <div className="animate-spin mx-auto mb-6 h-10 w-10 border-2 border-[#C8A84B] border-t-transparent rounded-full" />
          <p className="text-sm uppercase tracking-[0.32em] text-slate-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#F7F5F2] flex items-center justify-center px-6 py-12">
        <div className="max-w-lg w-full bg-white p-10 rounded-3xl border border-slate-200 shadow-lg">
          <h1 className="text-2xl font-semibold text-slate-900 mb-4">Profile unavailable</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/signin')} className="w-full">
            Sign In Again
          </Button>
        </div>
      </div>
    );
  }

  const planName = getPlanName(subscription);
  const planStatus = subscription?.subscription_status || 'FREE';
  const billingCycle = getBillingCycle(subscription);
  const renewalDate = getRenewalDate(subscription);
  const features = getSubscriptionFeatures(subscription);
  const accountCreated = formatDate(user?.created_at || user?.createdAt || user?.account_created_at || user?.created || user?.createdAt);
  const isAdmin = isAdminUser(user);

  const daysUntilRenewal = (dateValue) => {
    if (!dateValue) return null;
    const diff = new Date(dateValue) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const getRenewalSummary = () => {
    if (!subscription) return 'No active subscription';
    const targetDate = subscription.end_date || subscription.current_period_end || subscription.next_billing_date || null;
    if (!targetDate) {
      return subscription.billing_cycle ? `${subscription.billing_cycle.charAt(0).toUpperCase() + subscription.billing_cycle.slice(1)} billing` : 'Subscription details unavailable';
    }

    const days = daysUntilRenewal(targetDate);
    if (subscription.subscription_status === 'EXPIRED') {
      return `Expired on ${formatDate(targetDate)}`;
    }
    if (days === 0) {
      return `Expires today · Renews ${formatDate(targetDate)}`;
    }
    return `${days} day${days !== 1 ? 's' : ''} remaining · Renews ${formatDate(targetDate)}`;
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setProfileMsg({ type: '', text: '' });
    try {
      const API = getApiUrl();
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const resp = await fetch(`${API}/auth/profile`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify({ name: profileEdit.name.trim(), picture: profileEdit.picture.trim() }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setProfileMsg({ type: 'error', text: data.message || 'Failed to update profile.' });
        return;
      }
      const updated = await resp.json();
      setUser(updated);
      setProfileEdit({ name: updated.name || '', picture: updated.picture || '' });
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
    } catch {
      setProfileMsg({ type: 'error', text: 'Unable to connect. Please try again.' });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwMsg({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (pwForm.newPassword.length < 8) {
      setPwMsg({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }
    setPwSaving(true);
    setPwMsg({ type: '', text: '' });
    try {
      const API = getApiUrl();
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const resp = await fetch(`${API}/auth/password`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ current_password: pwForm.currentPassword, new_password: pwForm.newPassword }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setPwMsg({ type: 'error', text: data.message || 'Failed to change password.' });
        return;
      }
      setPwMsg({ type: 'success', text: data.message || 'Password changed successfully.' });
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch {
      setPwMsg({ type: 'error', text: 'Unable to connect. Please try again.' });
    } finally {
      setPwSaving(false);
    }
  };

  const handleToggleAutoRenewal = async () => {
    if (!subscription || autoRenewalLoading) return;
    setAutoRenewalLoading(true);
    try {
      const API = getApiUrl();
      const resp = await fetch(`${API}/payments/subscription/auto-renewal`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ auto_renewal: !subscription.auto_renewal }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setSubscription(data);
      }
    } catch (err) {
      console.error('Failed to toggle auto-renewal:', err);
    } finally {
      setAutoRenewalLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F2] pb-16">
      {paymentSuccess && (
        <div className="bg-green-50 border-b border-green-200 px-6 py-3 text-center text-sm font-medium text-green-800">
          🎉 Subscription activated! Your profile has been updated.
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-24">
        <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr] mb-10">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_32px_80px_rgba(15,23,42,0.08)]">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-5">
                <div className="h-24 w-24 rounded-[1.5rem] bg-slate-100 flex items-center justify-center text-3xl font-semibold text-slate-700">
                  {getAvatarText(user)}
                </div>
                <div>
                  <p className="text-3xl font-[Cormorant_Garamond] font-semibold text-slate-900">
                    {getDisplayName(user)}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">{user?.email}</p>
                </div>
              </div>
              <div className="rounded-3xl bg-slate-100 px-5 py-4 text-right">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Current plan</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">{planName}</p>
              </div>
            </div>

            <div className="mt-10 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_60px_rgba(15,23,42,0.08)]">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{planName}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <span>{toTitleCase(planStatus)}</span>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 ${
                      subscription?.subscription_status === 'ACTIVE'
                        ? 'bg-emerald-100 text-emerald-700'
                        : subscription?.subscription_status === 'EXPIRED'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-slate-100 text-slate-700'
                    }`}>
                      {subscription?.subscription_status ? toTitleCase(subscription.subscription_status) : 'Free'}
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-slate-600">{getRenewalSummary()}</p>
                </div>
                <div className="flex flex-col sm:items-end gap-3 sm:text-right">
                  {subscription?.subscription_status === 'ACTIVE' ? (
                    <div className="flex flex-col items-end gap-1.5">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                        subscription.auto_renewal
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-500'
                      }`}>
                        {subscription.auto_renewal ? 'Auto-renewal enabled' : 'Auto-renewal disabled'}
                      </span>
                      <button
                        type="button"
                        onClick={handleToggleAutoRenewal}
                        disabled={autoRenewalLoading}
                        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${
                          subscription.auto_renewal
                            ? 'bg-white text-slate-600 border-slate-300 hover:border-red-300 hover:text-red-600'
                            : 'bg-white text-emerald-700 border-emerald-300 hover:border-emerald-400 hover:bg-emerald-50'
                        }`}
                      >
                        {subscription.auto_renewal ? 'Disable auto-renewal' : 'Enable auto-renewal'}
                      </button>
                    </div>
                  ) : (
                    <a
                      href="/subscription"
                      className="inline-flex items-center justify-center rounded-full bg-[#D4AF37] px-4 py-2 text-sm font-semibold text-black hover:bg-[#C09B2B] transition"
                    >
                      Renew Plan
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              <div className="rounded-3xl bg-slate-50 p-5 border border-slate-100">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Account created</p>
                <p className="mt-3 text-base font-semibold text-slate-900">{accountCreated}</p>
              </div>
              <div className="rounded-3xl bg-slate-50 p-5 border border-slate-100">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Subscription status</p>
                <p className="mt-3 text-base font-semibold text-slate-900">{toTitleCase(planStatus)}</p>
              </div>
            </div>
          </div>

          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_32px_80px_rgba(15,23,42,0.08)]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Subscription details</p>
                <h2 className="mt-3 text-2xl font-semibold text-slate-900">Manage your plan</h2>
              </div>
              <div className="rounded-3xl bg-[#F8F2DF] px-4 py-3 text-sm font-semibold text-[#7C5F0F]">
                {billingCycle}
              </div>
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div className="rounded-3xl bg-slate-50 p-5 border border-slate-100">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Plan name</p>
                <p className="mt-3 text-base font-semibold text-slate-900">{planName}</p>
              </div>
              <div className="rounded-3xl bg-slate-50 p-5 border border-slate-100">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Renewal / expiry</p>
                <p className="mt-3 text-base font-semibold text-slate-900">{renewalDate !== 'N/A' ? formatDate(renewalDate) : 'N/A'}</p>
              </div>
            </div>

            <div className="mt-8 rounded-[1.75rem] bg-[#F8FAFC] border border-slate-100 p-6">
              <div className="flex items-center gap-3 text-slate-700">
                <Sparkles className="h-5 w-5" />
                <p className="text-sm font-semibold uppercase tracking-[0.28em] text-slate-500">
                  Included features
                </p>
              </div>
              <ul className="mt-5 grid gap-3">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 rounded-3xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                    <ShieldCheck className="mt-1 h-4 w-4 text-emerald-600" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Button
                onClick={() => navigate(`/subscription?plan=premium&billing=monthly`)}
                className="w-full sm:w-auto"
              >
                {planName.toLowerCase().includes('premium') ? 'View plans' : 'Upgrade to Premium'}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/subscription')}
                className="w-full sm:w-auto"
              >
                Manage subscription
              </Button>
            </div>

            <div className="mt-8 rounded-3xl border border-slate-100 bg-slate-50 px-6 py-5 text-sm text-slate-600">
              <div className="flex items-center gap-3 font-semibold text-slate-900 mb-3">
                <Clock3 className="h-4 w-4" />
                <span>Renewal cadence</span>
              </div>
              <p>
                Your subscription details are shown here for your account only. Any changes or renewals will be reflected immediately after updating your plan.
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_32px_80px_rgba(15,23,42,0.08)]">
            <div className="flex items-center gap-3 text-slate-700 mb-5">
              <UserCheck className="h-5 w-5" />
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-slate-500">Profile information</p>
            </div>
            <div className="space-y-4 text-sm text-slate-700">
              <div className="grid gap-2">
                <p className="font-medium text-slate-900">Display name</p>
                <p>{getDisplayName(user)}</p>
              </div>
              <div className="grid gap-2">
                <p className="font-medium text-slate-900">Email address</p>
                <p>{user?.email || 'Not available'}</p>
              </div>
              <div className="grid gap-2">
                <p className="font-medium text-slate-900">Account created</p>
                <p>{accountCreated}</p>
              </div>
              <div className="grid gap-2">
                <p className="font-medium text-slate-900">Current plan</p>
                <p>{planName}</p>
              </div>
            </div>
          </div>

          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_32px_80px_rgba(15,23,42,0.08)]">
            <div className="flex items-center gap-3 text-slate-700 mb-5">
              <ArrowRight className="h-5 w-5" />
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-slate-500">Subscription actions</p>
            </div>
            <div className="space-y-4 text-sm text-slate-700">
              <div className="rounded-3xl bg-slate-50 p-4 border border-slate-100">
                <p className="font-medium text-slate-900">Upgrade or change plan</p>
                <p className="mt-2 text-slate-600">Choose a different plan or billing cycle from the subscription page.</p>
              </div>
              <div className="rounded-3xl bg-slate-50 p-4 border border-slate-100">
                <p className="font-medium text-slate-900">Billing cycle</p>
                <p className="mt-2 text-slate-600">{billingCycle}</p>
              </div>
              <div className="rounded-3xl bg-slate-50 p-4 border border-slate-100">
                <p className="font-medium text-slate-900">Renewal date</p>
                <p className="mt-2 text-slate-600">{renewalDate !== 'N/A' ? formatDate(renewalDate) : 'N/A'}</p>
              </div>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => navigate('/admin')}
                  className="flex w-full items-center justify-between rounded-3xl border border-[#D4AF37]/50 bg-[#FFF9EA] p-4 text-left transition hover:border-[#D4AF37] hover:bg-[#FFF5D6]"
                  data-testid="profile-admin-tab"
                >
                  <span>
                    <span className="block font-semibold text-slate-900">Admin page</span>
                    <span className="mt-1 block text-slate-600">Manage uploaded intelligence reports.</span>
                  </span>
                  <ArrowRight className="h-5 w-5 flex-shrink-0 text-[#A77F16]" />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="grid gap-6 lg:grid-cols-2 mt-6">
          {/* Edit Profile */}
          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_32px_80px_rgba(15,23,42,0.08)]">
            <div className="flex items-center gap-3 text-slate-700 mb-5">
              <Pencil className="h-5 w-5" />
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-slate-500">Edit Profile</p>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-700 font-medium mb-1.5 block">Display Name</Label>
                <Input
                  type="text"
                  placeholder="Your name"
                  value={profileEdit.name}
                  onChange={(e) => setProfileEdit({ ...profileEdit, name: e.target.value })}
                  className="h-11 border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                />
              </div>
              <div>
                <Label className="text-slate-700 font-medium mb-1.5 block">Profile Picture URL</Label>
                <Input
                  type="url"
                  placeholder="https://..."
                  value={profileEdit.picture}
                  onChange={(e) => setProfileEdit({ ...profileEdit, picture: e.target.value })}
                  className="h-11 border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                />
              </div>
              {profileMsg.text && (
                <p className={`text-sm rounded-xl px-4 py-2.5 ${profileMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {profileMsg.text}
                </p>
              )}
              <Button
                onClick={handleSaveProfile}
                disabled={profileSaving}
                className="w-full h-11 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-semibold rounded-full disabled:opacity-60"
              >
                {profileSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>

          {/* Change Password */}
          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_32px_80px_rgba(15,23,42,0.08)]">
            <div className="flex items-center gap-3 text-slate-700 mb-5">
              <KeyRound className="h-5 w-5" />
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-slate-500">Change Password</p>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label className="text-slate-700 font-medium mb-1.5 block">Current Password</Label>
                <div className="relative">
                  <Input
                    type={showCurrent ? 'text' : 'password'}
                    placeholder="Enter current password"
                    value={pwForm.currentPassword}
                    onChange={(e) => setPwForm({ ...pwForm, currentPassword: e.target.value })}
                    className="h-11 pr-11 border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    required
                  />
                  <button type="button" onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-slate-700 font-medium mb-1.5 block">New Password</Label>
                <div className="relative">
                  <Input
                    type={showNew ? 'text' : 'password'}
                    placeholder="At least 8 characters"
                    value={pwForm.newPassword}
                    onChange={(e) => setPwForm({ ...pwForm, newPassword: e.target.value })}
                    className="h-11 pr-11 border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    required
                  />
                  <button type="button" onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-slate-700 font-medium mb-1.5 block">Confirm New Password</Label>
                <Input
                  type="password"
                  placeholder="Repeat new password"
                  value={pwForm.confirmPassword}
                  onChange={(e) => setPwForm({ ...pwForm, confirmPassword: e.target.value })}
                  className="h-11 border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                  required
                />
              </div>
              {pwMsg.text && (
                <p className={`text-sm rounded-xl px-4 py-2.5 ${pwMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {pwMsg.text}
                </p>
              )}
              <Button
                type="submit"
                disabled={pwSaving}
                className="w-full h-11 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-semibold rounded-full disabled:opacity-60"
              >
                {pwSaving ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
